import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Check, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "../api";
import { useApp } from "../context/AppContext";

/** Settings → Buddy: the installer's fixed helpers (the names the live interpreter announces). */
export const FixedHelpersEditor = () => {
  const { user, t } = useApp();
  const [fixed, setFixed] = useState([]);
  const [editing, setEditing] = useState(null); // index | "new" | null
  const [draft, setDraft] = useState("");

  useEffect(() => {
    if (!user?.id) return;
    api.get("/interpreter/helpers/fixed", { params: { user_id: user.id } })
      .then((r) => setFixed(r.data?.fixed || [])).catch(() => {});
  }, [user?.id]);

  const save = async (next) => {
    try {
      const r = await api.put("/interpreter/helpers/fixed", { user_id: user.id, fixed: next });
      setFixed(r.data?.fixed || next);
      toast.success(t("helpers_saved"));
    } catch (e) { toast.error(t("something_wrong")); }
  };

  const commit = async () => {
    const name = draft.trim();
    if (!name) { setEditing(null); return; }
    const next = editing === "new" ? [...fixed, name] : fixed.map((n, i) => (i === editing ? name : n));
    setEditing(null); setDraft("");
    await save(next);
  };

  const remove = (i) => save(fixed.filter((_, k) => k !== i));

  const inputRow = (
    <div className="flex items-center gap-2" data-testid="helpers-edit-row">
      <input autoFocus value={draft} onChange={(e) => setDraft(e.target.value)} maxLength={40}
        onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") { setEditing(null); setDraft(""); } }}
        placeholder={t("helpers_name_ph")} data-testid="helpers-name-input"
        className="flex-1 h-9 rounded-lg border bg-background px-3 text-sm" />
      <button type="button" onClick={commit} data-testid="helpers-save-btn" className="h-9 w-9 rounded-lg bg-primary text-primary-foreground flex items-center justify-center"><Check className="h-4 w-4" /></button>
      <button type="button" onClick={() => { setEditing(null); setDraft(""); }} data-testid="helpers-cancel-btn" className="h-9 w-9 rounded-lg border flex items-center justify-center"><X className="h-4 w-4" /></button>
    </div>
  );

  return (
    <div className="px-4 py-3 space-y-2 bg-muted/30" data-testid="fixed-helpers-editor">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("helpers_fixed_title")}</span>
        {editing !== "new" && fixed.length < 8 && (
          <button type="button" onClick={() => { setEditing("new"); setDraft(""); }} data-testid="helpers-add-btn"
            className="h-7 px-2.5 rounded-full bg-primary/10 text-primary text-xs font-semibold flex items-center gap-1">
            <Plus className="h-3.5 w-3.5" /> {t("helpers_add")}
          </button>
        )}
      </div>
      {fixed.length === 0 && editing !== "new" && (
        <p className="text-xs text-muted-foreground" data-testid="helpers-empty">{t("helpers_empty")}</p>
      )}
      <ul className="space-y-1.5">
        {fixed.map((n, i) => (
          <li key={`${n}-${i}`} data-testid={`helper-item-${i}`}>
            {editing === i ? inputRow : (
              <div className="flex items-center gap-2 rounded-lg bg-background border px-3 py-2">
                <span className="flex-1 text-sm font-medium truncate">{n}</span>
                <button type="button" onClick={() => { setEditing(i); setDraft(n); }} data-testid={`helper-edit-${i}`} className="h-8 w-8 rounded-md text-muted-foreground hover:bg-muted flex items-center justify-center"><Pencil className="h-4 w-4" /></button>
                <button type="button" onClick={() => remove(i)} data-testid={`helper-delete-${i}`} className="h-8 w-8 rounded-md text-destructive hover:bg-destructive/10 flex items-center justify-center"><Trash2 className="h-4 w-4" /></button>
              </div>
            )}
          </li>
        ))}
        {editing === "new" && <li>{inputRow}</li>}
      </ul>
    </div>
  );
};
